import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { seatLabel as seatLabelOf } from '@bre-ticketing/domain';
import { SeatMapView } from '@bre-ticketing/seatmap';
import type { AvailabilityMap, ManifestGeometry, SeatGeometry } from '@bre-ticketing/seatmap/core';
import type { ApiError, BalanceTerms, CartSeat, CartView, CustomerFlag, CustomerRecord, CustomerOffers, ExchangeContext, ExchangeDifference, ExchangeQuote, ExchangeResult, PaymentRequestInput, PerformanceDetail, SaleResult, Tender, WindowPackage, WindowPerformance } from '../api';
import { dollars, endpoints, readExchangeContext, when, writeExchangeContext } from '../api';
import { useManagerPin, usePermissions, useWindowContext } from '../session';
import { loadStripe } from '../stripe';
import { Button, ErrorNote } from '../ui';
import { PackageSeats } from './PackageSeats';
import { CustomerCreditLine, useCustomerCredit } from './CustomerCredit';
import { SeasonPassPanel, VoucherPanel } from './Comps';
import { dateWords } from '../terms';

/** Seats in a hold the cashier may sell out of (D-330). */
const HOLD_FILL = '#d99a2b';

/**
 * Sell (spec §34, D-56): pick the performance, tap seats on the map (or
 * best-available / GA by count), see the cart price itself on the window's
 * channel, discount or comp a seat with a manager's PIN when needed, attach
 * the customer, tender, and Complete Sale. The API is the truth for every
 * step; this screen only draws what it returns.
 */

type Layout = ManifestGeometry & { seat_units: Record<string, string>; ga_unit_counts: Record<string, number>; seat_zones: Record<string, string | null> };

const POLL_MS = 6000;

/**
 * Comp types last, wherever ticket types are listed (D-275).
 *
 * A comp sitting next to Adult in a dropdown is a free ticket one mis-tap away,
 * on a touch screen, at a busy window. Ordering is not a permission and does not
 * pretend to be one -- it is the difference between a mistake being possible and
 * being likely.
 *
 * One function because there are two lists: the selector and the price list. Two
 * orderings for the same thing is how one of them quietly goes back to normal.
 */
const compsLast = <T extends { is_comp_type: number }>(types: readonly T[]): T[] =>
  [...types].sort((a, b) => Number(a.is_comp_type === 1) - Number(b.is_comp_type === 1));

export default function SellPage() {
  const { performanceId } = useParams();
  const navigate = useNavigate();
  const [done, setDone] = useState<SaleResult | null>(null);
  const [exchangeDone, setExchangeDone] = useState<ExchangeResult | null>(null);
  const [exchange, setExchange] = useState<ExchangeContext | null>(() => readExchangeContext());
  const endExchange = () => {
    writeExchangeContext(null);
    setExchange(null);
  };
  if (done) return <SaleDone result={done} onNew={() => setDone(null)} />;
  if (exchangeDone) return <ExchangeDone result={exchangeDone} onNew={() => setExchangeDone(null)} />;
  return (
    <div className="space-y-4">
      {exchange ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-accent/40 bg-accent/10 px-4 py-3 text-sm">
          <span>
            <span className="font-semibold">Exchanging {exchange.tickets.length} ticket{exchange.tickets.length === 1 ? '' : 's'}</span> from <span className="font-mono">{exchange.order_number}</span>
            {exchange.customer_name ? ` (${exchange.customer_name})` : ''}: {exchange.tickets.map((t) => t.label).join('; ')}. Pick {exchange.tickets.length} replacement seat{exchange.tickets.length === 1 ? '' : 's'} for {exchange.event_title ?? 'the same event'}, then quote.
          </span>
          <button type="button" onClick={endExchange} className="touch rounded border border-line px-3 text-xs font-medium hover:bg-page">
            Cancel exchange
          </button>
        </div>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <div className="min-w-0 space-y-4">
          {performanceId ? <PerformanceSale performanceId={performanceId} onBack={() => void navigate('/sell')} /> : <PerformanceList onPick={(id) => void navigate(`/sell/${id}`)} />}
          {!exchange ? <CustomerOffersPanel performanceId={performanceId ?? null} /> : null}
          {/* Comp vouchers and season passes (D-373). */}
          {performanceId && !exchange ? <VoucherPanel performanceId={performanceId} /> : null}
          {performanceId && !exchange ? <SeasonPassPanel performanceId={performanceId} /> : null}
          {!performanceId && !exchange ? <PackageList /> : null}
        </div>
        <CartPanel
          onDone={setDone}
          exchange={exchange}
          onExchanged={(result) => {
            endExchange();
            setExchangeDone(result);
          }}
        />
      </div>
    </div>
  );
}

// --- What the customer already holds (D-167) ----------------------------------------------------

/**
 * Passes to redeem and seasons to renew, for the customer attached to the cart.
 *
 * Both were built for the website and could not be done at the window: a pass
 * holder who walked up instead of logging in, or a subscriber who came in to
 * renew in person, had to be turned away or sold something at full price. The
 * API calls are the same ones the account page makes, so the limits, the held
 * seats and the renewal deadline cannot come out differently here.
 *
 * It only appears when there is something to show — an empty panel on the sell
 * screen is a thing the cashier learns to look past.
 */
function CustomerOffersPanel({ performanceId }: { performanceId: string | null }) {
  const queryClient = useQueryClient();
  const cart = queryClient.getQueryData<CartView>(['cart']);
  const customerSub = cart?.customer?.sub ?? null;
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const offers = useQuery<CustomerOffers, ApiError>({ queryKey: ['customer-offers', customerSub], queryFn: endpoints.customerOffers, enabled: Boolean(customerSub) });
  const detail = useQuery<PerformanceDetail, ApiError>({ queryKey: ['performance', performanceId], queryFn: () => endpoints.performance(performanceId!), enabled: Boolean(performanceId) });

  const applied = (cartView: CartView, message: string) => {
    setError(null);
    setNotice(message);
    queryClient.setQueryData(['cart'], cartView);
    void queryClient.invalidateQueries({ queryKey: ['customer-offers', customerSub] });
  };
  const redeem = useMutation({
    mutationFn: (passId: string) => endpoints.redeemPass({ pass_id: passId, performance_id: performanceId!, quantity }),
    onSuccess: (result) => applied(result.cart, `Pass redeemed: ${result.unit_ids.length} seat${result.unit_ids.length === 1 ? '' : 's'} held at no ticket charge.`),
    onError: (cause: ApiError) => setError(cause.message),
  });
  const renew = useMutation({
    mutationFn: (instanceId: string) => endpoints.renew(instanceId),
    onSuccess: (result) => applied(result.cart, `${result.product.name} added, on last season's seats.`),
    onError: (cause: ApiError) => setError(cause.message),
  });

  if (!customerSub) return null;
  const eventId = detail.data?.performance.event_id ?? null;
  // A pass is only offered where it is actually good: the event has to be on it.
  const usable = (offers.data?.passes ?? []).filter((pass) => (eventId ? pass.events.some((e) => e.id === eventId) : false));
  const renewals = offers.data?.renewals ?? [];
  if (usable.length === 0 && renewals.length === 0) return null;

  return (
    <section className="rounded-xl border border-line bg-surface">
      <header className="border-b border-line px-5 py-3">
        <h2 className="font-semibold">This customer already holds</h2>
        <p className="text-xs text-ink-600">Passes redeem at no ticket charge; per-ticket fees are still collected. A renewal brings last season's seats forward.</p>
      </header>
      {error ? (
        <div className="px-5 pt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      {notice ? <p className="mx-5 mt-3 rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">{notice}</p> : null}
      <ul className="divide-y divide-line">
        {usable.map((pass) => {
          const left = pass.total_redemptions_allowed === null ? null : Math.max(0, pass.total_redemptions_allowed - pass.redeemed);
          return (
            <li key={pass.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{pass.name}</p>
                {pass.area_line ? <p className="text-xs font-semibold text-ink-900">{pass.area_line}.</p> : null}
                <p className="text-xs text-ink-600">
                  {left === null ? 'unlimited redemptions' : `${left} redemption${left === 1 ? '' : 's'} left`} · admits {pass.admissions_per_performance} per {dateWords().one} · valid to {when(pass.valid_to)}
                </p>
              </div>
              <select value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} className="touch rounded-lg border border-line px-2" aria-label="How many to redeem">
                {Array.from({ length: pass.admissions_per_performance }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <Button variant="primary" className="touch" disabled={redeem.isPending || left === 0} onClick={() => redeem.mutate(pass.id)}>
                Redeem
              </Button>
            </li>
          );
        })}
        {renewals.map((offer) => (
          <li key={offer.instance_id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
            <div className="min-w-0 flex-1">
              <p className="font-medium">
                Renew {offer.product.name} <span className="tabular text-ink-600">{offer.price_cents === null ? '' : dollars(offer.price_cents)}</span>
              </p>
              <p className="text-xs text-ink-600">
                {offer.seats.map((seat) => [seat.section, seat.row, seat.seat_number].filter(Boolean).join(' ')).join(', ') || 'same seats'}
                {offer.expires_at ? ` · offer holds until ${when(offer.expires_at)}` : ''}
              </p>
            </div>
            <Button variant="primary" className="touch" disabled={renew.isPending} onClick={() => renew.mutate(offer.instance_id)}>
              Renew
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// --- Packages (D-63) ----------------------------------------------------------------------------

function PackageList() {
  const queryClient = useQueryClient();
  const packages = useQuery({ queryKey: ['window-packages'], queryFn: endpoints.packages });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [picks, setPicks] = useState<Record<string, { quantity: number; selections: string[]; table_id: string }>>({});
  // Which package is being seated on the map, if any (D-330).
  const [seating, setSeating] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: (pkg: WindowPackage) => {
      const pick = picks[pkg.id] ?? { quantity: 1, selections: [], table_id: '' };
      return endpoints.addPackage({ package: pkg.code, quantity: pkg.package_kind === 'TABLE_SUBSCRIPTION' ? 1 : pick.quantity, ...(pkg.package_kind === 'CHOOSE_YOUR_OWN' ? { selections: pick.selections } : {}), seat: pkg.package_kind === 'TABLE_SUBSCRIPTION' ? { mode: 'table', table_id: pick.table_id } : { mode: 'best' } });
    },
    onSuccess: (result, pkg) => {
      setError(null);
      setNotice(`${pkg.name} added: ${result.cart.totals.tickets} seat${result.cart.totals.tickets === 1 ? '' : 's'} held.`);
      queryClient.setQueryData(['cart'], result.cart);
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const list = packages.data?.packages ?? [];
  if (packages.isPending) return null;
  return (
    <section className="rounded-xl border border-line bg-surface">
      <header className="border-b border-line px-5 py-3">
        <h2 className="font-semibold">Season tickets & packages</h2>
        <p className="text-xs text-ink-600">Add holds the best available seats for every {dateWords().one} in the package; Pick seats lets the customer choose on the map. A season ticket keeps the same seat throughout. A pass needs the customer attached first.</p>
      </header>
      {error ? (
        <div className="px-5 pt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      {notice ? <p className="mx-5 mt-3 rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">{notice}</p> : null}
      <ul className="divide-y divide-line">
        {list.length === 0 ? <li className="px-5 py-6 text-sm text-ink-400">No packages on sale.</li> : null}
        {list.map((pkg) => {
          const pick = picks[pkg.id] ?? { quantity: 1, selections: [], table_id: '' };
          const optional = pkg.components.filter((k) => k.required !== 1);
          return (
            <li key={pkg.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {pkg.name} <span className="tabular text-ink-600">{pkg.price_cents === null ? '' : dollars(pkg.price_cents)}</span>
                </p>
                <p className="text-xs text-ink-600">
                  {pkg.package_kind.replaceAll('_', ' ').toLowerCase()} · {pkg.components.length} {pkg.package_kind === 'MEMBERSHIP_PASS' ? 'event' : 'performance'}
                  {pkg.components.length === 1 ? '' : 's'}
                  {pkg.areas && pkg.areas.length > 0 ? ` · ${pkg.areas.map((area) => area.name).join(', ')} only` : ''}
                </p>
                {pkg.package_kind === 'CHOOSE_YOUR_OWN' ? (
                  <div className="mt-1 flex flex-wrap gap-2 text-xs">
                    {optional.map((k) => (
                      <label key={k.id} className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={pick.selections.includes(k.id)}
                          onChange={(e) => setPicks({ ...picks, [pkg.id]: { ...pick, selections: e.target.checked ? [...pick.selections, k.id] : pick.selections.filter((id) => id !== k.id) } })}
                        />
                        {k.event_title} {k.performance_starts_at ? when(k.performance_starts_at, k.timezone ?? undefined) : ''}
                      </label>
                    ))}
                  </div>
                ) : null}
              </div>
              {pkg.package_kind === 'TABLE_SUBSCRIPTION' ? (
                <TablePicker code={pkg.code} value={pick.table_id} onChange={(id) => setPicks({ ...picks, [pkg.id]: { ...pick, table_id: id } })} />
              ) : (
                <select value={pick.quantity} onChange={(e) => setPicks({ ...picks, [pkg.id]: { ...pick, quantity: Number(e.target.value) } })} className="touch rounded-lg border border-line px-2" aria-label="How many">
                  {Array.from({ length: Math.min(pkg.max_per_order, 10) }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              )}
              {pkg.package_kind !== 'MEMBERSHIP_PASS' && pkg.package_kind !== 'TABLE_SUBSCRIPTION' ? (
                <Button
                  className="touch"
                  disabled={pkg.price_cents === null || (pkg.package_kind === 'CHOOSE_YOUR_OWN' && pick.selections.length < (pkg.min_selections ?? 1))}
                  onClick={() => setSeating(seating === pkg.id ? null : pkg.id)}
                >
                  Pick seats
                </Button>
              ) : null}
              <Button variant="primary" className="touch" disabled={add.isPending || pkg.price_cents === null} onClick={() => add.mutate(pkg)}>
                Add
              </Button>
              {seating === pkg.id ? (
                <div className="w-full">
                  <PackageSeats
                    pkg={pkg}
                    quantity={pick.quantity}
                    selections={pkg.package_kind === 'CHOOSE_YOUR_OWN' ? pick.selections : []}
                    onCancel={() => setSeating(null)}
                    onHeld={(cart) => {
                      setSeating(null);
                      setError(null);
                      setNotice(`${pkg.name} added: ${cart.totals.tickets} seat${cart.totals.tickets === 1 ? '' : 's'} held.`);
                      queryClient.setQueryData(['cart'], cart);
                    }}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function TablePicker({ code, value, onChange }: { code: string; value: string; onChange: (id: string) => void }) {
  const tables = useQuery({ queryKey: ['package-tables', code], queryFn: () => endpoints.packageTables(code) });
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="touch rounded-lg border border-line px-2" aria-label="Table">
      <option value="">Table…</option>
      {(tables.data?.tables ?? []).map((t) => (
        <option key={t.id} value={t.id} disabled={t.available < t.seats}>
          {t.section ? `${t.section} · ` : ''}Table {t.label} ({t.seats}){t.available < t.seats ? ' · taken' : ''}
        </option>
      ))}
    </select>
  );
}

// --- Performances ---------------------------------------------------------------------------

function PerformanceList({ onPick }: { onPick: (id: string) => void }) {
  const [all, setAll] = useState(false);
  const performances = useQuery({ queryKey: ['performances', all], queryFn: () => endpoints.performances(all) });
  return (
    <section className="rounded-xl border border-line bg-surface">
      <header className="flex items-center justify-between border-b border-line px-5 py-3">
        <h1 className="text-base font-semibold">On sale</h1>
        <label className="flex items-center gap-2 text-sm text-ink-600">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> All venues
        </label>
      </header>
      <ul className="divide-y divide-line">
        {(performances.data?.performances ?? []).map((p) => (
          <li key={p.id}>
            <button type="button" disabled={!p.sellable} onClick={() => onPick(p.id)} className="touch flex w-full items-center justify-between gap-4 px-5 py-3 text-left hover:bg-page disabled:opacity-50">
              <span>
                <span className="block font-medium">{p.event_title}</span>
                <span className="block text-sm text-ink-600">
                  {when(p.starts_at, p.timezone, 'long')} · {p.venue_name}
                </span>
              </span>
              <span className={`rounded px-2 py-0.5 text-xs font-semibold ${p.sellable ? 'bg-ok-soft text-ok' : 'bg-page text-ink-400'}`}>{p.sellable ? 'Sell' : p.status.toLowerCase().replaceAll('_', ' ')}</span>
            </button>
          </li>
        ))}
        {performances.data && performances.data.performances.length === 0 ? <li className="px-5 py-6 text-sm text-ink-400">Nothing on sale.</li> : null}
      </ul>
    </section>
  );
}

// --- The map --------------------------------------------------------------------------------

function PerformanceSale({ performanceId, onBack }: { performanceId: string; onBack: () => void }) {
  const queryClient = useQueryClient();
  const detail = useQuery<PerformanceDetail, ApiError>({ queryKey: ['performance', performanceId], queryFn: () => endpoints.performance(performanceId) });
  // Never cached: an entry time filling up between two sales is the whole risk.
  const slots = useQuery({ queryKey: ['timed-entry', performanceId], queryFn: () => endpoints.timedEntry(performanceId), staleTime: 0 });
  const [layout, setLayout] = useState<Layout | null>(null);
  const [availability, setAvailability] = useState<Awaited<ReturnType<typeof endpoints.availability>> | null>(null);
  const [ticketTypeId, setTicketTypeId] = useState<string>('');
  // One, not two: a walk-up is more often a single than a pair, and a cashier
  // who has to correct the default every time stops reading it.
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [listOnly, setListOnly] = useState(false);
  const busy = useRef(false);

  useEffect(() => {
    if (!detail.data) return;
    if (!ticketTypeId && detail.data.ticket_types[0]) setTicketTypeId(detail.data.ticket_types[0].id);
    fetch(detail.data.layout_url, { credentials: 'same-origin' })
      .then(async (r) => (r.ok ? ((await r.json()) as Layout) : null))
      .then((data) => setLayout(data))
      .catch(() => setLayout(null));
  }, [detail.data, ticketTypeId]);

  const refresh = useCallback(async () => {
    try {
      setAvailability(await endpoints.availability(performanceId));
    } catch {
      // next poll
    }
  }, [performanceId]);
  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  const unitToSeat = useMemo(() => {
    const map = new Map<string, string>();
    if (layout) for (const [seatId, unitId] of Object.entries(layout.seat_units)) map.set(unitId, seatId);
    return map;
  }, [layout]);
  const availabilityMap = useMemo<AvailabilityMap>(() => {
    const out: Record<string, 'AVAILABLE' | 'HELD_BY_YOU' | 'UNAVAILABLE' | 'ACCESSIBLE_ON_REQUEST'> = {};
    for (const unit of availability?.units ?? []) {
      const seatId = unitToSeat.get(unit.unit_id);
      if (seatId) out[seatId] = unit.availability;
    }
    return out;
  }, [availability, unitToSeat]);
  /*
   * Seats in a hold this cashier may sell out of (D-330): amber on the map,
   * and tappable like an open seat. Only the ones not already in somebody's
   * cart, which the map reports as taken.
   */
  const holdOfSeat = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const hold of availability?.holds ?? []) {
      for (const unitId of hold.unit_ids) {
        const seatId = unitToSeat.get(unitId);
        if (seatId && availabilityMap[seatId] === 'UNAVAILABLE') map.set(seatId, { id: hold.id, name: hold.name });
      }
    }
    return map;
  }, [availability, unitToSeat, availabilityMap]);
  const seatFills = useMemo(() => {
    const fills: Record<string, string> = {};
    if (!layout || !detail.data) return fills;
    const zoneColour = new Map(detail.data.zones.map((z) => [z.id, z.color_hex ?? '#c9ced8']));
    for (const seat of layout.seats) {
      const zone = layout.seat_zones[seat.id];
      if (availabilityMap[seat.id] === 'AVAILABLE' && zone) fills[seat.id] = zoneColour.get(zone) ?? '#c9ced8';
      else if (holdOfSeat.has(seat.id)) fills[seat.id] = HOLD_FILL;
    }
    return fills;
  }, [layout, detail.data, availabilityMap, holdOfSeat]);
  const heldSeats = useMemo(() => new Set(Object.entries(availabilityMap).filter(([, a]) => a === 'HELD_BY_YOU').map(([id]) => id)), [availabilityMap]);

  // The same query the page above already runs (react-query shares by key), so
  // this re-renders when a hold lands rather than reading a stale snapshot.
  const cartForMap = useQuery<CartView, ApiError>({ queryKey: ['cart'], queryFn: endpoints.cart });

  /*
   * How many places in each area are already in this sale (D-245). The map
   * draws them in a different color, which is the only way a cashier can tell
   * "six on the lawn" from "no lawn yet" without counting the cart.
   */
  const gaInCart = useMemo(() => {
    const counts = new Map<string, number>();
    for (const performance of cartForMap.data?.performances ?? []) {
      for (const seat of performance.seats) {
        if (performance.performance_id !== performanceId || !seat.ga_area_id) continue;
        counts.set(seat.ga_area_id, (counts.get(seat.ga_area_id) ?? 0) + 1);
      }
    }
    return counts;
  }, [cartForMap.data, performanceId]);

  const afterCart = (cart: CartView) => {
    queryClient.setQueryData(['cart'], cart);
    void refresh();
  };
  const hold = useMutation({
    mutationFn: (input: Parameters<typeof endpoints.hold>[0]) => endpoints.hold(input),
    onSuccess: (result) => {
      setError(null);
      afterCart(result.cart);
    },
    onError: (cause: ApiError) => setError(cause.message),
    onSettled: () => {
      busy.current = false;
    },
  });
  const release = useMutation({
    mutationFn: (unitIds: string[]) => endpoints.release(performanceId, unitIds),
    onSuccess: (result) => afterCart(result.cart),
    onError: (cause: ApiError) => setError(cause.message),
    onSettled: () => {
      busy.current = false;
    },
  });

  const onSeatClick = (seat: SeatGeometry) => {
    if (!layout || busy.current || !ticketTypeId) return;
    // A table sold whole is held whole (D-152). The Durable Object refuses a
    // partial hold whichever surface asks, so the window takes the table in one
    // tap rather than showing the cashier a refusal they then have to work
    // around seat by seat.
    const whole = seat.table_id && layout.tables.some((t) => t.id === seat.table_id && t.sell_mode === 'WHOLE_TABLE');
    const seats = whole ? layout.seats.filter((s) => s.table_id === seat.table_id) : [seat];
    const unitIds = seats.map((s) => layout.seat_units[s.id]).filter((id): id is string => Boolean(id));
    if (unitIds.length === 0) return;
    busy.current = true;
    if (heldSeats.has(seat.id)) release.mutate(unitIds);
    else if (seats.every((s) => availabilityMap[s.id] === 'AVAILABLE' || availabilityMap[s.id] === 'ACCESSIBLE_ON_REQUEST' || holdOfSeat.has(s.id))) {
      hold.mutate({ performance_id: performanceId, ticket_type_id: ticketTypeId, mode: 'seats', unit_ids: unitIds });
    } else busy.current = false;
  };

  /*
   * A general-admission area is on the map, not only in the button row (D-245).
   *
   * D-184 drew GA areas as real shapes and let the storefront sell from them;
   * the window kept a row of buttons, which is a different picture of the same
   * room. A cashier looking at the map could see the lawn and not be able to
   * sell it, and the two lists drift the moment somebody adds an area.
   *
   * Simpler here than on the storefront: the ticket type is already chosen in
   * the header, so a tap is "hold `quantity` of that type in this area". The
   * buttons stay — a busy window is faster with a button than with a pointer,
   * and the room may have no drawing at all.
   */
  const onGaAreaClick = (area: { id: string }) => {
    if (!ticketTypeId || hold.isPending) return;
    hold.mutate({ performance_id: performanceId, ticket_type_id: ticketTypeId, mode: 'ga', ga_area_id: area.id, quantity });
  };

  if (detail.error) return <ErrorNote>{detail.error.message}</ErrorNote>;
  if (!detail.data) return <p className="text-sm text-ink-400">Loading…</p>;
  const p = detail.data.performance;
  const gaAvailable = new Map((availability?.ga_areas ?? []).map((a) => [a.ga_area_id, a.available_quantity]));
  /*
   * A timed ticket type is sold against an entry time, not an area (D-232).
   *
   * The window offered the GA buttons and nothing else, so a cashier selling a
   * timed show either held with no entry time or could not sell it at all —
   * while the storefront had picked entry times since D-86. The same public
   * endpoint feeds both: the times are public, and a second reader of the same
   * list is a second list to drift.
   */
  const timedType = detail.data.ticket_types.find((t) => t.id === ticketTypeId && t.timed_entry === 1) ?? null;
  return (
    <section className="rounded-xl border border-line bg-surface">
      <header className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
        <button type="button" onClick={onBack} className="touch rounded px-2 text-sm text-ink-600 hover:bg-page">
          ← Shows
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold">{p.event_title}</h1>
          <p className="text-xs text-ink-600">
            {when(p.starts_at, p.timezone, 'long')} · {p.venue_name}
          </p>
        </div>
        <select value={ticketTypeId} onChange={(e) => setTicketTypeId(e.target.value)} className="touch rounded-lg border border-line px-3 text-sm" aria-label="Ticket type">
          {/*
            Comp types last, always (D-275). They sort wherever the catalog puts
            them otherwise, and a comp sitting next to Adult in a dropdown is a
            free ticket one mis-tap away -- on a touch screen, at a busy window.
            Ordering is not a permission, but it is the difference between a
            mistake being possible and being likely.
          */}
          {compsLast(detail.data.ticket_types).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </header>
      {error ? (
        <div className="px-5 pt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2 px-5 py-3 text-sm">
        <span className="text-ink-600">Quick pick</span>
        <input type="number" min={1} max={20} value={quantity} onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))} className="touch w-16 rounded-lg border border-line px-2 text-center" aria-label="Quantity" />
        <Button className="touch" disabled={hold.isPending || !ticketTypeId} onClick={() => hold.mutate({ performance_id: performanceId, ticket_type_id: ticketTypeId, mode: 'best', quantity, allow_split: false })}>
          Best available together
        </Button>
        <Button className="touch" disabled={hold.isPending || !ticketTypeId} onClick={() => hold.mutate({ performance_id: performanceId, ticket_type_id: ticketTypeId, mode: 'best', quantity, allow_split: true })}>
          Best available, any
        </Button>
        {timedType
          ? (slots.data?.slots ?? []).map((slot) => (
              <Button
                key={slot.id}
                className="touch"
                // Sold out or closed is not something to find out after taking the money.
                disabled={hold.isPending || slot.available_quantity < quantity || slot.status === 'SOLD_OUT' || slot.status === 'CLOSED'}
                onClick={() => hold.mutate({ performance_id: performanceId, ticket_type_id: ticketTypeId, mode: 'timed', timed_entry_slot_id: slot.id, quantity })}
              >
                {when(slot.starts_at, slots.data?.timezone ?? p.timezone, 'time')} ({slot.available_quantity} left)
              </Button>
            ))
          : (layout?.ga_areas ?? []).map((area) => (
              <Button key={area.id} className="touch" disabled={hold.isPending || !ticketTypeId} onClick={() => hold.mutate({ performance_id: performanceId, ticket_type_id: ticketTypeId, mode: 'ga', ga_area_id: area.id, quantity })}>
                {area.name} ({gaAvailable.get(area.id) ?? '…'} left)
              </Button>
            ))}
        <label className="ml-auto flex items-center gap-1 text-ink-600">
          <input type="checkbox" checked={listOnly} onChange={(e) => setListOnly(e.target.checked)} /> Prices
        </label>
      </div>
      {listOnly ? (
        <div className="grid gap-2 px-5 pb-4 sm:grid-cols-2 md:grid-cols-3">
          {detail.data.zones.map((zone) => (
            <div key={zone.id} className="rounded-lg border border-line p-3 text-sm">
              <p className="flex items-center gap-2 font-medium">
                <span className="inline-block h-3 w-3 rounded-full" style={{ background: zone.color_hex ?? '#c9ced8' }} /> {zone.name}
              </p>
              {compsLast(detail.data.ticket_types).map((t) => {
                const price = detail.data!.prices[zone.id]?.[t.id];
                return (
                  <p key={t.id} className="flex justify-between text-ink-600">
                    <span>{t.name}</span>
                    <span className="tabular">{price ? `${dollars(price.face_cents)} (${dollars(price.total_cents)} all-in)` : '—'}</span>
                  </p>
                );
              })}
            </div>
          ))}
        </div>
      ) : layout ? (
        <div className="px-3 pb-3">
          <SeatMapView layout={layout} mode="view" backgroundUrl={layout.manifest.background_url ?? null} availability={availabilityMap} seatFills={seatFills} selectedSeatIds={heldSeats} onSeatClick={onSeatClick} gaAvailable={gaAvailable} gaInCart={gaInCart} onGaAreaClick={onGaAreaClick} height={520} className="rounded-lg border border-line" />
          {/* The lawn is on the picture now (D-245), so the hint has to say so:
              a cashier who reads "tap a seat" will keep using the buttons. */}
          <p className="mt-2 text-xs text-ink-400">
            Tap a seat to hold it for this sale; tap again to let it go. Colored seats are open, by price zone; gray ones are taken.
            {holdOfSeat.size > 0 ? ' Amber seats are in a hold, and you may sell them.' : ''}
            {(layout.ga_areas ?? []).length > 0 ? ` Tap a general-admission area to hold ${quantity} there.` : ''}
          </p>
          {/* Sell a whole hold in one tap: the usual case is "the band's
              guest list is here, give them their six seats" (D-330). */}
          {(availability?.holds ?? []).some((h) => h.unit_ids.some((u) => holdOfSeat.has(unitToSeat.get(u) ?? ''))) ? (
            <div className="mt-3 rounded-lg border border-line p-3">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">Holds on this {dateWords().one}</p>
              <ul className="mt-2 space-y-1.5 text-sm">
                {(availability?.holds ?? []).map((h) => {
                  const open = h.unit_ids.filter((u) => holdOfSeat.has(unitToSeat.get(u) ?? ''));
                  if (open.length === 0) return null;
                  return (
                    <li key={h.id} className="flex flex-wrap items-center justify-between gap-2">
                      <span>
                        <span className="mr-2 inline-block h-3 w-3 rounded-full align-middle" style={{ background: HOLD_FILL }} aria-hidden="true" />
                        {h.name} <span className="text-ink-500">· {open.length} seat{open.length === 1 ? '' : 's'}</span>
                      </span>
                      <Button
                        disabled={hold.isPending || !ticketTypeId}
                        onClick={() => {
                          busy.current = true;
                          hold.mutate({ performance_id: performanceId, ticket_type_id: ticketTypeId, mode: 'seats', unit_ids: open });
                        }}
                      >
                        Add all {open.length} to the sale
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="px-5 pb-4 text-sm text-ink-400">Loading the seat map…</p>
      )}
    </section>
  );
}

// --- The cart -------------------------------------------------------------------------------

function CartPanel({ onDone, exchange, onExchanged }: { onDone: (result: SaleResult) => void; exchange: ExchangeContext | null; onExchanged: (result: ExchangeResult) => void }) {
  const queryClient = useQueryClient();
  const can = usePermissions();
  const askPin = useManagerPin();
  const cart = useQuery<CartView, ApiError>({ queryKey: ['cart'], queryFn: endpoints.cart, refetchInterval: 30_000 });
  const [tendering, setTendering] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerQuery, setCustomerQuery] = useState('');
  // Typed at the window, cleared when it lands on the cart (D-250).
  const [orgDraft, setOrgDraft] = useState('');
  const [walkUp, setWalkUp] = useState({ name: '', email: '', phone: '' });
  const matches = useQuery({ queryKey: ['customers', customerQuery], queryFn: () => endpoints.customers(customerQuery), enabled: customerQuery.trim().length >= 2 });
  const refreshCart = (next?: CartView) => {
    if (next) queryClient.setQueryData(['cart'], next);
    else void queryClient.invalidateQueries({ queryKey: ['cart'] });
  };

  const withApproval = async <T,>(reason: string, attempt: (pin?: string) => Promise<T>): Promise<T> => {
    try {
      return await attempt();
    } catch (cause) {
      if ((cause as ApiError).code !== 'MANAGER_APPROVAL_REQUIRED') throw cause;
      const pin = await askPin(reason);
      if (!pin) throw new Error('Not approved');
      return attempt(pin);
    }
  };
  const discount = useMutation({
    mutationFn: async (seat: CartSeat) => {
      const raw = window.prompt(`Discount for ${seatLabel(seat)} (dollars off, or % like 10%)`, seat.discount_cents ? (seat.discount_cents / 100).toFixed(2) : '');
      if (raw === null) return null;
      const trimmed = raw.trim();
      const input = trimmed === '' ? { discount_cents: 0 } : trimmed.endsWith('%') ? { discount_percent: Number(trimmed.slice(0, -1)) } : { discount_cents: Math.round(Number(trimmed) * 100) };
      return withApproval(`Discount on ${seatLabel(seat)}: ${trimmed || 'remove'}`, (pin) => endpoints.item(seat.unit_id, { ...input, ...(pin ? { manager_pin: pin } : {}) }));
    },
    onSuccess: (result) => {
      setError(null);
      if (result) refreshCart(result.cart);
    },
    onError: (cause: Error) => setError(cause.message),
  });
  const comp = useMutation({
    mutationFn: async ({ seat, reasonId, reasonName }: { seat: CartSeat; reasonId: string | null; reasonName: string }) => withApproval(`Comp ${seatLabel(seat)} (${reasonName})`, (pin) => endpoints.item(seat.unit_id, { comp_reason_id: reasonId, ...(pin ? { manager_pin: pin } : {}) })),
    onSuccess: (result) => {
      setError(null);
      refreshCart(result.cart);
    },
    onError: (cause: Error) => setError(cause.message),
  });
  const release = useMutation({
    mutationFn: (seat: CartSeat & { performance_id: string }) => endpoints.release(seat.performance_id, [seat.unit_id]),
    onSuccess: (result) => refreshCart(result.cart),
    onError: (cause: ApiError) => setError(cause.message),
  });
  const clear = useMutation({ mutationFn: endpoints.clearCart, onSuccess: () => refreshCart() });
  const removePackage = useMutation({ mutationFn: (key: string) => endpoints.removePackage(key), onSuccess: (result) => refreshCart(result.cart), onError: (cause: ApiError) => setError(cause.message) });
  /*
   * What is known about this buyer (D-270).
   *
   * A chargeback, a suspected fraud or a do-not-sell has been recorded since
   * migration 0042 and was only ever visible on the disputes screen -- not here,
   * with the person standing at the counter. It **informs and never blocks**:
   * the design note in 0042 is explicit that a flag is a note about a buyer and
   * not a punishment, so a cashier reads it and decides.
   */
  const [flags, setFlags] = useState<CustomerFlag[]>([]);
  // And what staff have written about them (D-358); warned, never blocked.
  const [record, setRecord] = useState<CustomerRecord | null>(null);
  /*
   * A real customer record for a walk-up (D-370), so the next visit finds them
   * by name, email or phone, and signing in later with that address makes the
   * record theirs.
   */
  const createCustomer = useMutation({
    mutationFn: () => endpoints.createCustomer({ name: walkUp.name.trim(), email: walkUp.email.trim() || null, phone: walkUp.phone.trim() || null }),
    onSuccess: (result) => {
      setWalkUp({ name: '', email: '', phone: '' });
      setCustomer.mutate({ sub: result.customer.sub, name: result.customer.name, email: result.customer.email, phone: result.customer.phone });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const setCustomer = useMutation({
    mutationFn: (input: Parameters<typeof endpoints.setCustomer>[0]) => endpoints.setCustomer(input),
    onSuccess: (result) => {
      setCustomerQuery('');
      setOrgDraft('');
      setFlags(result.flags ?? []);
      setRecord(result.record ?? null);
      refreshCart();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  /*
   * A promo code the customer hands over (D-359): a shared code or a
   * single-use one. It is used up when the sale completes, not when it is
   * typed, so a sale that is abandoned leaves it good.
   */
  const [promoDraft, setPromoDraft] = useState('');
  const applyPromo = useMutation({
    mutationFn: () => endpoints.applyPromoCode(promoDraft.trim()),
    onSuccess: (result) => {
      setPromoDraft('');
      setError(null);
      refreshCart(result.cart);
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const removePromo = useMutation({ mutationFn: endpoints.removePromoCode, onSuccess: (result) => refreshCart(result.cart), onError: (cause: ApiError) => setError(cause.message) });
  // Extras (D-377): a promotion's concession special, sold beside its game as online.
  const setAddOns = useMutation({ mutationFn: endpoints.setAddOns, onSuccess: (result) => refreshCart(result.cart), onError: (cause: ApiError) => setError(cause.message) });
  const chooseAddOn = (productId: string, quantity: number) => {
    const others = (cart.data?.add_ons ?? []).filter((line) => line.add_on_product_id !== productId).map((line) => ({ add_on_product_id: line.add_on_product_id, quantity: line.quantity }));
    setAddOns.mutate(quantity > 0 ? [...others, { add_on_product_id: productId, quantity }] : others);
  };
  const compReasons = useQuery({ queryKey: ['comp-reasons', cart.data?.performances[0]?.performance_id], queryFn: () => endpoints.performance(cart.data!.performances[0]!.performance_id), enabled: Boolean(cart.data?.performances[0]) });

  const view = cart.data;
  const total = view?.totals.customer_total_cents ?? 0;
  const seats = view?.performances.flatMap((p) => p.seats.map((s) => ({ ...s, performance_id: p.performance_id, title: p.title }))) ?? [];
  return (
    <aside className="flex flex-col rounded-xl border border-line bg-surface lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)]">
      <header className="flex items-center justify-between border-b border-line px-4 py-3">
        <h2 className="font-semibold">This sale</h2>
        {seats.length > 0 ? (
          <button type="button" onClick={() => clear.mutate()} className="text-xs text-ink-400 underline">
            Clear
          </button>
        ) : null}
      </header>
      {error ? (
        <div className="px-4 pt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      <div className="flex-1 overflow-y-auto px-4 py-3 text-sm">
        {seats.length === 0 && (view?.packages ?? []).length === 0 ? <p className="text-ink-400">Tap seats on the map, or use a quick pick.</p> : null}
        {(view?.packages ?? []).length > 0 ? (
          <ul className="mb-2 space-y-1">
            {view!.packages.map((pkg) => (
              <li key={pkg.key} className="flex items-center justify-between rounded-lg border border-accent/40 bg-accent/10 px-2 py-1">
                <span>
                  <span className="font-medium">{pkg.name}</span>
                  {pkg.quantity > 1 ? ` × ${pkg.quantity}` : ''}
                  <span className="block text-xs text-ink-600">{pkg.unit_ids.length > 0 ? `${pkg.unit_ids.length} seats at the package price` : 'pass, issued to the customer'}</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="tabular font-semibold">{dollars(pkg.total_cents)}</span>
                  <button type="button" onClick={() => removePackage.mutate(pkg.key)} className="touch rounded px-2 text-xs text-ink-400 hover:text-bad">
                    Remove
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        <ul className="space-y-2">
          {seats.map((seat) => (
            <li key={seat.unit_id} className="rounded-lg border border-line p-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium">{seatLabel(seat)}</p>
                  <p className="text-xs text-ink-600">
                    {seat.title} · {seat.ticket_type_name}
                    {seat.comp_reason_name ? ` · comp (${seat.comp_reason_name})` : seat.discount_cents ? ` · −${dollars(seat.discount_cents)}${seat.package_key ? ' package' : seat.pass_id ? ' pass' : ''}` : ''}
                  </p>
                </div>
                <p className="tabular font-semibold">{seat.total_cents === null ? '—' : dollars(seat.total_cents)}</p>
              </div>
              <div className="mt-1 flex flex-wrap gap-1 text-xs">
                <button type="button" onClick={() => discount.mutate(seat)} className="touch rounded border border-line px-2 hover:bg-page">
                  Discount
                </button>
                {seat.comp_reason_id ? (
                  <button type="button" onClick={() => comp.mutate({ seat, reasonId: null, reasonName: 'remove' })} className="touch rounded border border-line px-2 hover:bg-page">
                    Un-comp
                  </button>
                ) : (
                  <select
                    value=""
                    onChange={(e) => {
                      const reason = compReasons.data?.comp_reasons.find((r) => r.id === e.target.value);
                      if (reason) comp.mutate({ seat, reasonId: reason.id, reasonName: reason.name });
                    }}
                    className="touch rounded border border-line px-1"
                    aria-label="Comp"
                  >
                    <option value="">Comp…</option>
                    {(compReasons.data?.comp_reasons ?? []).map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                )}
                <button type="button" onClick={() => release.mutate(seat)} className="touch ml-auto rounded px-2 text-ink-400 hover:text-bad">
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>

        {exchange || seats.length === 0 ? null : (
          <div className="mt-3 border-t border-line pt-3">
            {/* Automatic deals on these games (D-377): applied with no code, each on its own line. */}
            {(view?.automatic_promotions ?? []).map((deal) => (
              <p key={deal.promotion_id} className="mb-1 flex items-center justify-between rounded-lg bg-page px-2 py-1 text-xs">
                <span>
                  <strong className="text-ink-900">{deal.name}</strong> · automatic · −{dollars(deal.discount_cents)} on {deal.tickets} ticket{deal.tickets === 1 ? '' : 's'}
                </span>
              </p>
            ))}
            <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Promo code</p>
            {view?.promotion ? (
              <p className="mt-1 flex items-center justify-between rounded-lg bg-page px-2 py-1 text-xs">
                <span>
                  <strong className="font-mono text-ink-900">{view.promotion.code}</strong> · {view.promotion.name} · −{dollars(view.promotion.discount_cents)} on {view.promotion.tickets} ticket{view.promotion.tickets === 1 ? '' : 's'}
                </span>
                <button type="button" onClick={() => removePromo.mutate()} className="text-ink-400 underline">
                  Remove
                </button>
              </p>
            ) : view?.promo_code_entered ? (
              <p className="mt-1 flex items-center justify-between rounded-lg bg-page px-2 py-1 text-xs">
                <span>
                  <strong className="font-mono text-ink-900">{view.promo_code_entered}</strong> · kept, not applied
                </span>
                <button type="button" onClick={() => removePromo.mutate()} className="text-ink-400 underline">
                  Remove
                </button>
              </p>
            ) : (
              <form
                className="mt-1 flex gap-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (promoDraft.trim()) applyPromo.mutate();
                }}
              >
                <input value={promoDraft} onChange={(e) => setPromoDraft(e.target.value)} placeholder="Code" aria-label="Promo code" className="touch w-full rounded-lg border border-line px-2 font-mono text-sm uppercase" />
                <Button className="touch" type="submit" disabled={!promoDraft.trim() || applyPromo.isPending}>
                  Apply
                </Button>
              </form>
            )}
            {/* A code and a deal never combine: say which one the customer is getting (D-377). */}
            {view?.promotion_choice ? <p className="mt-1 text-xs text-ink-600">{view.promotion_choice.message}</p> : null}
            {view?.promotion_removed_reason === 'PROMO_AUTOMATIC_ENDED' ? <p className="mt-1 text-xs text-warn">A deal on these tickets has ended; the total changed.</p> : null}
            {(view?.add_ons_offered ?? []).length > 0 ? (
              <div className="mt-3">
                <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Extras</p>
                <ul className="mt-1 space-y-1">
                  {(view?.add_ons_offered ?? []).map((product) => {
                    const chosen = view?.add_ons?.find((line) => line.add_on_product_id === product.id)?.quantity ?? 0;
                    const most = Math.min(product.max_per_order, product.remaining ?? product.max_per_order);
                    return (
                      <li key={product.id} className="flex items-center justify-between gap-2 text-xs">
                        <span>
                          <strong className="text-ink-900">{product.name}</strong> · {dollars(product.price_cents)}
                          {product.remaining !== null ? <span className="text-ink-400"> · {product.remaining} left</span> : null}
                        </span>
                        <span className="flex items-center gap-1">
                          <button type="button" className="touch rounded border border-line px-2" aria-label={`One fewer ${product.name}`} disabled={chosen === 0 || setAddOns.isPending} onClick={() => chooseAddOn(product.id, chosen - 1)}>
                            −
                          </button>
                          <span className="tabular w-6 text-center">{chosen}</span>
                          <button type="button" className="touch rounded border border-line px-2" aria-label={`One more ${product.name}`} disabled={chosen >= most || setAddOns.isPending} onClick={() => chooseAddOn(product.id, chosen + 1)}>
                            +
                          </button>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        )}

        {exchange ? null : (
        <div className="mt-4 border-t border-line pt-3">
          <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Customer</p>
          {view?.customer ? (
            <>
            <p className="mt-1 flex items-center justify-between">
              <span>
                {view.customer.name ?? 'Guest'}
                <span className="block text-xs text-ink-600">{[view.customer.email, view.customer.phone].filter(Boolean).join(' · ')}</span>
                <CustomerCreditLine sub={view.customer.sub} />
              </span>
              <button type="button" onClick={() => setCustomer.mutate({})} className="text-xs text-ink-400 underline">
                Change
              </button>
            </p>
            {/*
              What is known about this buyer (D-270). Recorded since migration
              0042 and never shown here, which is the one place it matters.
              `warns_at_sale` decides how loudly; nothing is blocked, because a
              flag is a note about a buyer and not a punishment.
            */}
            {(record?.warnings ?? []).map((warning) => (
              <p key={warning.tag_code} role="alert" className="mt-1 rounded-lg border border-bad/40 bg-bad-soft px-2 py-1 text-xs text-bad">
                <strong>Tagged “{warning.name}”. You can still sell; read this first.</strong>
                <span className="block">{warning.note ? `“${warning.note.body}” (${warning.note.author_name ?? 'staff'})` : 'Nobody wrote down why. Ask a manager before going ahead.'}</span>
              </p>
            ))}
            {record && (record.tags.length > 0 || record.notes_count > 0) ? (
              <p className="mt-1 text-xs text-ink-600">
                {record.tags.filter((tag) => !tag.warns_at_sale).map((tag) => (
                  <span key={tag.code} className="mr-1 inline-block rounded bg-accent/10 px-1.5 font-semibold text-accent">
                    {tag.name}
                  </span>
                ))}
                {record.latest_note ? <span className="block">{record.notes_count} note{record.notes_count === 1 ? '' : 's'}. Latest: “{record.latest_note.body}”</span> : null}
              </p>
            ) : null}
            {flags.map((flag) => (
              <p
                key={flag.id}
                className={`mt-1 rounded-lg px-2 py-1 text-xs ${flag.warns_at_sale ? 'border border-bad/40 bg-bad-soft text-bad' : 'bg-page text-ink-600'}`}
              >
                <strong>{flag.name}</strong>
                <span className="block">{flag.reason}</span>
              </p>
            ))}
            {/*
              Whose seats these are, when that is not the person on the phone
              (D-250). A sponsor's table is handed to guests who have never
              heard of the contact, so the organization is what prints — while
              the contact stays who to e-mail and who is at will call.
            */}
            {view.customer.organization_name ? (
              <p className="mt-1 flex items-center justify-between rounded-lg bg-page px-2 py-1 text-xs">
                <span>
                  Tickets print as <strong className="text-ink-900">{view.customer.organization_name}</strong>
                </span>
                <button
                  type="button"
                  onClick={() => setCustomer.mutate({ sub: view.customer!.sub, name: view.customer!.name, email: view.customer!.email, phone: view.customer!.phone, organization_name: null })}
                  className="text-ink-400 underline"
                >
                  Remove
                </button>
              </p>
            ) : (
              <details className="mt-1 text-xs">
                <summary className="cursor-pointer text-ink-400">These seats are for an organization</summary>
                <div className="mt-1 flex gap-1">
                  <input
                    value={orgDraft}
                    onChange={(e) => setOrgDraft(e.target.value)}
                    placeholder="Organization name (prints on the tickets)"
                    className="touch w-full rounded-lg border border-line px-2 text-sm"
                  />
                  <Button
                    className="touch"
                    disabled={orgDraft.trim().length === 0 || setCustomer.isPending}
                    onClick={() =>
                      setCustomer.mutate({
                        sub: view.customer!.sub,
                        name: view.customer!.name,
                        email: view.customer!.email,
                        phone: view.customer!.phone,
                        organization_name: orgDraft.trim(),
                      })
                    }
                  >
                    Use
                  </Button>
                </div>
              </details>
            )}
            </>
          ) : (
            <div className="mt-1 space-y-1">
              <input value={customerQuery} onChange={(e) => setCustomerQuery(e.target.value)} placeholder="Search name, phone, email, order #" className="touch w-full rounded-lg border border-line px-3" aria-label="Find a customer" />
              {(matches.data?.matches ?? []).slice(0, 6).map((m, i) => (
                <button key={i} type="button" onClick={() => setCustomer.mutate({ sub: m.sub, name: m.name, email: m.email, phone: m.phone })} className="touch flex w-full items-center justify-between rounded-lg px-3 text-left hover:bg-page">
                  <span>
                    {m.name ?? 'Unnamed'} <span className="text-xs text-ink-400">{m.source === 'account' ? (m.verified ? 'account' : 'account, not signed in yet') : `order ${m.last_order_number ?? ''}`}</span>
                  </span>
                  <span className="text-xs text-ink-600">{m.email ?? (m.phone_last4 ? `…${m.phone_last4}` : '')}</span>
                </button>
              ))}
              <details className="text-xs">
                <summary className="cursor-pointer text-ink-400">New customer or walk-up</summary>
                <div className="mt-1 space-y-1">
                  <input value={walkUp.name} onChange={(e) => setWalkUp({ ...walkUp, name: e.target.value })} placeholder="Name" className="touch w-full rounded-lg border border-line px-3 text-sm" aria-label="Name" />
                  <input value={walkUp.email} onChange={(e) => setWalkUp({ ...walkUp, email: e.target.value })} placeholder="Email (for e-tickets)" className="touch w-full rounded-lg border border-line px-3 text-sm" aria-label="Email" />
                  <input value={walkUp.phone} onChange={(e) => setWalkUp({ ...walkUp, phone: e.target.value })} placeholder="Phone" className="touch w-full rounded-lg border border-line px-3 text-sm" aria-label="Phone" />
                  <div className="flex gap-1">
                    <Button className="touch" onClick={() => setCustomer.mutate({ name: walkUp.name || null, email: walkUp.email || null, phone: walkUp.phone || null })}>
                      Use for this sale
                    </Button>
                    {can('customers.create') ? (
                      <Button
                        className="touch"
                        disabled={!walkUp.name.trim() || !(walkUp.email.trim() || walkUp.phone.trim()) || createCustomer.isPending}
                        onClick={() => createCustomer.mutate()}
                      >
                        Save as customer
                      </Button>
                    ) : null}
                  </div>
                </div>
              </details>
            </div>
          )}
        </div>
        )}
      </div>
      <footer className="border-t border-line px-4 py-3">
        {view && (view.totals.discount_cents > 0 || view.totals.comp_cents > 0 || view.totals.fees_cents > 0) ? (
          <dl className="mb-2 space-y-0.5 text-xs text-ink-600">
            <div className="flex justify-between">
              <dt>Face value</dt>
              <dd className="tabular">{dollars(view.totals.face_value_cents)}</dd>
            </div>
            {view.totals.discount_cents > 0 ? (
              <div className="flex justify-between">
                <dt>Discounts</dt>
                <dd className="tabular">−{dollars(view.totals.discount_cents)}</dd>
              </div>
            ) : null}
            {view.totals.comp_cents > 0 ? (
              <div className="flex justify-between">
                <dt>Comps</dt>
                <dd className="tabular">−{dollars(view.totals.comp_cents)}</dd>
              </div>
            ) : null}
            <div className="flex justify-between">
              <dt>Fees & tax</dt>
              <dd className="tabular">{dollars(view.totals.fees_cents + view.totals.tax_cents)}</dd>
            </div>
          </dl>
        ) : null}
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-ink-600">
            {view?.totals.tickets ?? 0} ticket{view?.totals.tickets === 1 ? '' : 's'}
          </span>
          <span className="tabular text-2xl font-semibold">{dollars(total)}</span>
        </div>
        {exchange ? (
          <Button variant="primary" className="touch mt-3 w-full justify-center text-base" disabled={!view || view.totals.tickets !== exchange.tickets.length || !can('box_office.sell')} onClick={() => setQuoting(true)}>
            {view && view.totals.tickets !== exchange.tickets.length ? `Pick ${exchange.tickets.length} seat${exchange.tickets.length === 1 ? '' : 's'} (${view.totals.tickets} held)` : 'Quote exchange'}
          </Button>
        ) : (
          <Button variant="primary" className="touch mt-3 w-full justify-center text-base" disabled={!view || view.totals.tickets === 0 || !can('box_office.sell')} onClick={() => setTendering(true)}>
            {total === 0 && (view?.totals.tickets ?? 0) > 0 ? 'Complete (no charge)' : 'Tender'}
          </Button>
        )}
      </footer>
      {tendering && view ? (
        <TenderPanel
          cart={view}
          onClose={() => setTendering(false)}
          allowBalance
          onSubmit={async (tenders, delivery, balance) => {
            const result = await endpoints.sale({ tenders, delivery: { method: delivery.willCall ? 'WILL_CALL' : delivery.print ? 'BOX_OFFICE_PRINT' : delivery.email ? 'EMAIL_PDF' : 'MOBILE', print: delivery.print && !delivery.willCall, email: delivery.email, receipt: delivery.receipt }, balance: balance?.terms ?? null, payment_request: balance?.payment_request ?? null });
            setTendering(false);
            refreshCart();
            onDone(result);
          }}
        />
      ) : null}
      {quoting && view && exchange ? (
        <ExchangePanel
          cart={view}
          exchange={exchange}
          onClose={() => setQuoting(false)}
          onDone={(result) => {
            setQuoting(false);
            refreshCart();
            onExchanged(result);
          }}
        />
      ) : null}
    </aside>
  );
}

/*
 * The window's copy of the shared rule (D-139), which had no table in it: a
 * cashier selling a table event saw "Table · Seat 3" and could not tell the
 * customer in front of them which table. The level/section prefix stays,
 * because it is what the window uses to find a seat in a big room.
 */
const seatLabel = (seat: CartSeat) => {
  const where = seatLabelOf({
    section: seat.area && seat.section && seat.section !== seat.area ? `${seat.area} ${seat.section.replace(`${seat.area} `, '')}` : (seat.section ?? seat.area),
    table: seat.table,
    row: seat.row,
    seat: seat.seat_number,
    ga_area: null,
  });
  // On a timed show the entry time is the thing that differs between two
  // otherwise identical lines, so it belongs on the line (D-232).
  return seat.entry_time ? `${where} · ${seat.entry_time}` : where;
};

// --- Tendering ------------------------------------------------------------------------------

type TenderDelivery = { print: boolean; email: boolean; receipt: boolean; willCall: boolean };

type BalanceChoice = { terms: BalanceTerms; payment_request: PaymentRequestInput | null } | null;

export function TenderPanel({ cart, onClose, onSubmit, amount, title, submitLabel, showDelivery = true, allowBalance = false }: { cart: CartView; onClose: () => void; onSubmit: (tenders: Tender[], delivery: TenderDelivery, balance?: BalanceChoice) => Promise<void>; amount?: number; title?: string; submitLabel?: string; showDelivery?: boolean; allowBalance?: boolean }) {
  const can = usePermissions();
  const context = useWindowContext();
  /*
   * Tenders this deployment has at all (D-369). Gift cards and loyalty rewards
   * were BRE services and are switched off for STGD; the prior-system tender
   * belongs to the hidden migration tools. Unknown means absent.
   */
  const features = useQuery({ queryKey: ['box-office-features'], queryFn: endpoints.features, staleTime: 30_000 });
  const total = amount ?? cart.totals.customer_total_cents;
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [kind, setKind] = useState<'CASH' | 'CHECK' | 'CARD_TERMINAL' | 'CARD_KEYED' | 'HOUSE_ACCOUNT' | 'GIFT_CARD' | 'LOYALTY_REWARD' | 'PRIOR_SYSTEM' | 'ACCOUNT_CREDIT'>('CASH');
  // The attached customer's account credit (D-372), a sale tender only (`allowBalance` marks the sale).
  const creditOffered = allowBalance && features.data?.account_credit === true && Boolean(cart.customer?.sub);
  const accountCredit = useCustomerCredit(cart.customer?.sub, creditOffered).data?.credit ?? null;
  // What the previous system called the sale being recorded (D-251).
  const [priorReference, setPriorReference] = useState('');
  // Gift cards and rewards at the window (D-75).
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCard, setGiftCard] = useState<{ masked_identifier: string; available_cents: number } | null>(null);
  const [rewards, setRewards] = useState<{ reward_id: string; name: string; value_cents: number }[]>([]);
  const [cashGiven, setCashGiven] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [orgId, setOrgId] = useState('');
  const [delivery, setDelivery] = useState({ print: Boolean(context.data?.session?.ticket_printer_id), email: Boolean(cart.customer?.email), receipt: Boolean(context.data?.session?.receipt_printer_id), willCall: false });
  const [card, setCard] = useState<{ intent_id: string; status: string; message: string | null; keyed: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Paying over time (D-71): the whole thing now, a deposit, or nothing yet with a due date and a link.
  const [payFor, setPayFor] = useState<'FULL' | 'PARTIAL' | 'NONE'>('FULL');
  const [dueDate, setDueDate] = useState('');
  const [issuance, setIssuance] = useState<BalanceTerms['issuance_policy']>('ISSUE_WHEN_PAID');
  const [sendLink, setSendLink] = useState(true);
  const [linkAmount, setLinkAmount] = useState<'FULL_REMAINING_BALANCE_ONLY' | 'CUSTOM_AMOUNT_ALLOWED'>('CUSTOM_AMOUNT_ALLOWED');
  const balanceAllowed = allowBalance && can('orders.balance_due');
  const elementsRef = useRef<{ stripe: Awaited<ReturnType<typeof loadStripe>>; elements: ReturnType<Awaited<ReturnType<typeof loadStripe>>['elements']> } | null>(null);
  const tendered = tenders.reduce((s, t) => s + t.amount_cents, 0);
  const remaining = Math.max(0, (payFor === 'NONE' ? 0 : total) - tendered);
  const cashApplied = Math.min(remaining, Math.round(Number(cashGiven || '0') * 100));
  const changeDue = Math.max(0, Math.round(Number(cashGiven || '0') * 100) - remaining);

  const addTender = (tender: Tender) => setTenders([...tenders, tender]);

  const startCard = async (keyed: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const started = await endpoints.cardStart(remaining, keyed);
      setCard({ intent_id: started.intent.payment_intent_id, status: keyed ? 'keying' : 'collecting', message: null, keyed });
      if (keyed && started.intent.client_secret && started.publishable_key) {
        const stripe = await loadStripe(started.publishable_key);
        const elements = stripe.elements({ clientSecret: started.intent.client_secret });
        elementsRef.current = { stripe, elements };
        window.setTimeout(() => elements.create('payment', { layout: 'tabs' }).mount('#keyed-card'), 0);
      }
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const confirmKeyed = async () => {
    if (!elementsRef.current || !card) return;
    setBusy(true);
    const { stripe, elements } = elementsRef.current;
    const result = await stripe.confirmPayment({ elements, confirmParams: { return_url: window.location.href }, redirect: 'if_required' });
    setBusy(false);
    if (result.error) return setError(result.error.message ?? 'The card was declined');
    setCard({ ...card, status: 'ready' });
    addTender({ tender_type: 'CARD_KEYED', amount_cents: remaining, payment_intent_id: card.intent_id });
  };
  useEffect(() => {
    if (!card || card.keyed || card.status !== 'collecting') return;
    const id = window.setInterval(async () => {
      try {
        const status = await endpoints.cardStatus(card.intent_id);
        if (status.ready) {
          window.clearInterval(id);
          setCard({ ...card, status: 'ready', message: status.card ? `${(status.card.brand ?? 'card').toUpperCase()} ····${status.card.last4 ?? ''}` : null });
          addTender({ tender_type: 'CARD_TERMINAL', amount_cents: remaining, payment_intent_id: card.intent_id });
        } else if (status.failure) {
          window.clearInterval(id);
          setCard({ ...card, status: 'failed', message: status.failure });
        }
      } catch {
        // keep polling
      }
    }, 1500);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.intent_id, card?.status]);

  const complete = async () => {
    setBusy(true);
    setError(null);
    try {
      const owing = balanceAllowed && payFor !== 'FULL';
      const dueAt = dueDate ? new Date(`${dueDate}T23:59:59`).toISOString() : null;
      const balance: BalanceChoice = owing
        ? {
            terms: { issuance_policy: issuance, due_at: dueAt, past_due_policy: 'NOTIFY_STAFF' },
            payment_request: sendLink && cart.customer?.email ? { amount_policy: linkAmount, due_at: dueAt, recipient_email: cart.customer.email, recipient_name: cart.customer.name ?? null, send: true } : null,
          }
        : null;
      await onSubmit(tenders, delivery, balance);
    } catch (cause) {
      setError((cause as Error).message);
      setBusy(false);
    }
  };

  const terminal = context.data?.session?.payment_terminal_id ? (context.data.terminals.find((t) => t.id === context.data!.session!.payment_terminal_id) ?? null) : null;
  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-shell-900/60 p-4" role="dialog" aria-modal="true" aria-labelledby="tender-title">
      <div className="max-h-full w-full max-w-lg overflow-y-auto rounded-xl bg-surface p-5">
        <div className="flex items-baseline justify-between">
          <h2 id="tender-title" className="text-lg font-semibold">
            {title ?? 'Tender'}
          </h2>
          <span className="tabular text-2xl font-semibold">{dollars(total)}</span>
        </div>
        {error ? (
          <div className="mt-3">
            <ErrorNote>{error}</ErrorNote>
          </div>
        ) : null}
        {balanceAllowed ? (
          <div className="mt-3 space-y-2 rounded-lg border border-line bg-page p-3 text-sm">
            <div className="flex flex-wrap gap-3">
              <label className="flex items-center gap-1"><input type="radio" name="pay-for" checked={payFor === 'FULL'} onChange={() => setPayFor('FULL')} /> Pay in full</label>
              <label className="flex items-center gap-1"><input type="radio" name="pay-for" checked={payFor === 'PARTIAL'} onChange={() => setPayFor('PARTIAL')} /> Deposit now</label>
              <label className="flex items-center gap-1"><input type="radio" name="pay-for" checked={payFor === 'NONE'} onChange={() => { setPayFor('NONE'); setTenders([]); }} /> Balance due, nothing now</label>
            </div>
            {payFor !== 'FULL' ? (
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-1">
                  Due
                  <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="rounded border border-line px-2 py-1" />
                </label>
                <label className="flex items-center gap-1">
                  Tickets
                  <select value={issuance} onChange={(e) => setIssuance(e.target.value as BalanceTerms['issuance_policy'])} className="rounded border border-line px-2 py-1">
                    <option value="ISSUE_WHEN_PAID">issue when paid</option>
                    <option value="ISSUE_BUT_DISABLE_ADMISSION_UNTIL_PAID">issue, no entry until paid</option>
                    <option value="ISSUE_IMMEDIATELY">issue now</option>
                  </select>
                </label>
                {can('orders.payment_requests.manage') ? (
                  <label className="flex items-center gap-1">
                    <input type="checkbox" checked={sendLink} disabled={!cart.customer?.email} onChange={(e) => setSendLink(e.target.checked)} /> Email a payment link{cart.customer?.email ? '' : ' (needs a customer email)'}
                  </label>
                ) : null}
                {sendLink && cart.customer?.email ? (
                  <select value={linkAmount} onChange={(e) => setLinkAmount(e.target.value as typeof linkAmount)} className="rounded border border-line px-2 py-1">
                    <option value="CUSTOM_AMOUNT_ALLOWED">any amount up to the balance</option>
                    <option value="FULL_REMAINING_BALANCE_ONLY">the full balance only</option>
                  </select>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {tenders.length > 0 ? (
          <ul className="mt-3 divide-y divide-line rounded-lg border border-line text-sm">
            {tenders.map((t, i) => (
              <li key={i} className="flex items-center justify-between px-3 py-2">
                <span>
                  {t.tender_type.replaceAll('_', ' ').toLowerCase()}
                  {'check_number' in t ? ` #${t.check_number}` : ''}
                  {'cash_tendered_cents' in t && t.cash_tendered_cents ? ` (tendered ${dollars(t.cash_tendered_cents)})` : ''}
                </span>
                <span className="flex items-center gap-3">
                  <span className="tabular">{dollars(t.amount_cents)}</span>
                  {!('payment_intent_id' in t) ? (
                    <button type="button" onClick={() => setTenders(tenders.filter((_, j) => j !== i))} className="text-xs text-ink-400 underline">
                      remove
                    </button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        {remaining > 0 ? (
          <>
            <p className="mt-3 text-sm text-ink-600">
              Remaining <span className="tabular font-semibold text-ink-900">{dollars(remaining)}</span>
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {(['CASH', 'CARD_TERMINAL', 'CARD_KEYED', 'CHECK', 'GIFT_CARD', 'LOYALTY_REWARD', 'ACCOUNT_CREDIT', 'HOUSE_ACCOUNT', 'PRIOR_SYSTEM'] as const)
                .filter((k) =>
                  k === 'CARD_TERMINAL'
                    ? Boolean(terminal)
                    : k === 'CARD_KEYED'
                      ? context.data?.register?.allow_manual_card === 1 && can('box_office.manual_card')
                      : k === 'CHECK'
                        ? can('box_office.check')
                        : k === 'HOUSE_ACCOUNT'
                          ? can('box_office.house_account')
                          : k === 'GIFT_CARD'
                            ? features.data?.gift_cards === true
                            : k === 'LOYALTY_REWARD'
                              ? features.data?.loyalty === true && Boolean(cart.customer?.sub)
                              : k === 'ACCOUNT_CREDIT'
                                ? creditOffered && (accountCredit?.balance_cents ?? 0) > 0 && !tenders.some((t) => t.tender_type === 'ACCOUNT_CREDIT')
                              : k === 'PRIOR_SYSTEM'
                                ? // Admin-only and rarely wanted (D-251): absent from
                                  // the row for anybody who cannot use it.
                                  features.data?.history_import === true && can('migration.import')
                                : Boolean(context.data?.shift),
                )
                .map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => {
                      setKind(k);
                      if (k === 'LOYALTY_REWARD' && cart.customer?.sub && rewards.length === 0) {
                        void endpoints
                          .loyaltyRewards(cart.customer.sub)
                          .then((r) => setRewards(r.rewards))
                          .catch((e: Error) => setError(e.message));
                      }
                    }}
                    className={`touch rounded-lg px-3 text-sm font-medium ${kind === k ? 'bg-accent text-white' : 'border border-line hover:bg-page'}`}
                  >
                    {{ CASH: 'Cash', CARD_TERMINAL: 'Card (reader)', CARD_KEYED: 'Card (keyed)', CHECK: 'Check', GIFT_CARD: 'Gift card', LOYALTY_REWARD: 'Reward', ACCOUNT_CREDIT: 'Account credit', PRIOR_SYSTEM: 'Prior system', HOUSE_ACCOUNT: 'House account' }[k]}
                  </button>
                ))}
              {!context.data?.shift ? <span className="self-center text-xs text-warn">Open your drawer to take cash.</span> : null}
            </div>
            <div className="mt-3 space-y-2 text-sm">
              {kind === 'CASH' ? (
                <>
                  <div className="flex flex-wrap gap-1">
                    {[remaining, Math.ceil(remaining / 500) * 500, Math.ceil(remaining / 1000) * 1000, Math.ceil(remaining / 2000) * 2000, Math.ceil(remaining / 10000) * 10000].filter((v, i, a) => a.indexOf(v) === i).map((v) => (
                      <button key={v} type="button" onClick={() => setCashGiven((v / 100).toFixed(2))} className="touch rounded-lg border border-line px-3 tabular hover:bg-page">
                        {dollars(v)}
                      </button>
                    ))}
                  </div>
                  <input type="number" step="0.01" inputMode="decimal" value={cashGiven} onChange={(e) => setCashGiven(e.target.value)} placeholder="Cash tendered" className="touch w-full rounded-lg border border-line px-3 text-lg tabular" aria-label="Cash tendered" />
                  {changeDue > 0 ? <p className="tabular text-ink-600">Change due {dollars(changeDue)}</p> : null}
                  <Button variant="primary" className="touch w-full justify-center" disabled={cashApplied <= 0} onClick={() => addTender({ tender_type: 'CASH', amount_cents: cashApplied, cash_tendered_cents: Math.round(Number(cashGiven) * 100) })}>
                    Apply {dollars(cashApplied)} cash
                  </Button>
                </>
              ) : null}
              {kind === 'CHECK' ? (
                <>
                  <input value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} placeholder="Check number" className="touch w-full rounded-lg border border-line px-3" aria-label="Check number" />
                  <Button variant="primary" className="touch w-full justify-center" disabled={!checkNumber.trim()} onClick={() => addTender({ tender_type: 'CHECK', amount_cents: remaining, check_number: checkNumber.trim() })}>
                    Apply {dollars(remaining)} by check
                  </Button>
                </>
              ) : null}
              {kind === 'PRIOR_SYSTEM' ? (
                <>
                  {/*
                    Money already taken somewhere else (D-251). Not a payment
                    being made now, so the drawer is untouched and the books
                    post it to the clearing account rather than counting the
                    income a second time.
                  */}
                  <p className="text-xs text-ink-600">Paid for in the previous system. Nothing is collected now and the drawer is not affected.</p>
                  <input
                    value={priorReference}
                    onChange={(e) => setPriorReference(e.target.value)}
                    placeholder="What the old system called it (order or transaction number)"
                    className="touch w-full rounded-lg border border-line px-3"
                  />
                  <Button
                    variant="primary"
                    className="touch w-full justify-center"
                    disabled={!priorReference.trim()}
                    onClick={() => addTender({ tender_type: 'PRIOR_SYSTEM', amount_cents: remaining, reference: priorReference.trim() })}
                  >
                    Record {dollars(remaining)} as already settled
                  </Button>
                </>
              ) : null}
              {kind === 'GIFT_CARD' ? (
                <>
                  <input value={giftCardCode} onChange={(e) => { setGiftCardCode(e.target.value); setGiftCard(null); }} placeholder="Gift card code" className="touch w-full rounded-lg border border-line px-3" aria-label="Gift card code" />
                  {giftCard ? <p className="tabular text-ink-600">{giftCard.masked_identifier} · {dollars(giftCard.available_cents)} available</p> : null}
                  {giftCard ? (
                    <Button variant="primary" className="touch w-full justify-center" onClick={() => addTender({ tender_type: 'GIFT_CARD', amount_cents: Math.min(giftCard.available_cents, remaining), gift_card_code: giftCardCode.trim() })}>
                      Apply {dollars(Math.min(giftCard.available_cents, remaining))} from the gift card
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      className="touch w-full justify-center"
                      disabled={giftCardCode.trim().length < 4 || busy}
                      onClick={() => {
                        setBusy(true);
                        setError(null);
                        endpoints
                          .checkGiftCard(giftCardCode.trim())
                          .then(setGiftCard)
                          .catch((e: Error) => setError(e.message))
                          .finally(() => setBusy(false));
                      }}
                    >
                      Check the card
                    </Button>
                  )}
                </>
              ) : null}
              {kind === 'LOYALTY_REWARD' ? (
                rewards.length === 0 ? (
                  <p className="text-ink-600">No rewards on this customer’s account.</p>
                ) : (
                  <div className="space-y-1">
                    {rewards.map((r) => (
                      <Button key={r.reward_id} className="touch w-full justify-between" disabled={r.value_cents > remaining} onClick={() => addTender({ tender_type: 'LOYALTY_REWARD', amount_cents: r.value_cents, reward_id: r.reward_id })}>
                        <span>{r.name}</span>
                        <span className="tabular">{dollars(r.value_cents)}</span>
                      </Button>
                    ))}
                    <p className="text-xs text-ink-400">A reward is used whole; it cannot be part-spent.</p>
                  </div>
                )
              ) : null}
              {kind === 'ACCOUNT_CREDIT' && accountCredit ? (
                accountCredit.verified ? (
                  <Button variant="primary" className="touch w-full justify-center" disabled={Math.min(accountCredit.spendable_cents, remaining) <= 0} onClick={() => addTender({ tender_type: 'ACCOUNT_CREDIT', amount_cents: Math.min(accountCredit.spendable_cents, remaining) })}>
                    Apply {dollars(Math.min(accountCredit.spendable_cents, remaining))} account credit
                  </Button>
                ) : (
                  <p className="text-ink-600">{dollars(accountCredit.balance_cents)} is on this account, but it can be spent only once the customer has signed in online with their email.</p>
                )
              ) : null}
              {kind === 'HOUSE_ACCOUNT' ? (
                <>
                  <input value={orgId} onChange={(e) => setOrgId(e.target.value)} placeholder="Organization id" className="touch w-full rounded-lg border border-line px-3" aria-label="Organization" />
                  <Button variant="primary" className="touch w-full justify-center" disabled={!orgId.trim()} onClick={() => addTender({ tender_type: 'HOUSE_ACCOUNT', amount_cents: remaining, organization_id: orgId.trim() })}>
                    Charge {dollars(remaining)} to the house account
                  </Button>
                </>
              ) : null}
              {kind === 'CARD_TERMINAL' ? (
                card && !card.keyed ? (
                  <div className="rounded-lg border border-line p-3">
                    <p className="font-medium">{card.status === 'collecting' ? `Waiting for the card on ${terminal?.label ?? 'the reader'}…` : card.status === 'failed' ? `Failed: ${card.message}` : `Authorized ${card.message ?? ''}`}</p>
                    <div className="mt-2 flex gap-2">
                      {terminal?.simulated && card.status === 'collecting' ? (
                        <Button className="touch" onClick={() => void endpoints.simulateTap().catch((e: Error) => setError(e.message))}>
                          Simulate tap
                        </Button>
                      ) : null}
                      {card.status !== 'ready' ? (
                        <Button
                          className="touch"
                          onClick={() => {
                            void endpoints.cardCancel(card.intent_id);
                            setCard(null);
                          }}
                        >
                          Cancel
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <Button variant="primary" className="touch w-full justify-center" disabled={busy} onClick={() => void startCard(false)}>
                    Take {dollars(remaining)} on {terminal?.label ?? 'the reader'}
                  </Button>
                )
              ) : null}
              {kind === 'CARD_KEYED' ? (
                card?.keyed ? (
                  <div>
                    <div id="keyed-card" className="rounded-lg border border-line p-3" />
                    <Button variant="primary" className="touch mt-2 w-full justify-center" disabled={busy || card.status === 'ready'} onClick={() => void confirmKeyed()}>
                      Authorize {dollars(remaining)}
                    </Button>
                  </div>
                ) : (
                  <Button variant="primary" className="touch w-full justify-center" disabled={busy} onClick={() => void startCard(true)}>
                    Key a card for {dollars(remaining)}
                  </Button>
                )
              ) : null}
            </div>
          </>
        ) : null}
        {showDelivery ? (
        <div className="mt-4 space-y-1 border-t border-line pt-3 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={delivery.willCall} onChange={(e) => setDelivery({ ...delivery, willCall: e.target.checked })} /> Hold at will call (print at pickup)
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={delivery.print && !delivery.willCall} disabled={delivery.willCall || !context.data?.session?.ticket_printer_id} onChange={(e) => setDelivery({ ...delivery, print: e.target.checked })} /> Print tickets now
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={delivery.email} disabled={!cart.customer?.email} onChange={(e) => setDelivery({ ...delivery, email: e.target.checked })} /> Email tickets {cart.customer?.email ? `to ${cart.customer.email}` : '(no email on the customer)'}
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={delivery.receipt} disabled={!context.data?.session?.receipt_printer_id} onChange={(e) => setDelivery({ ...delivery, receipt: e.target.checked })} /> Print a receipt
          </label>
        </div>
        ) : null}
        <div className="mt-4 flex gap-2">
          <Button variant="primary" className="touch flex-1 justify-center text-base" disabled={busy || (payFor === 'FULL' && remaining > 0) || (payFor === 'PARTIAL' && (tendered <= 0 || tendered >= total))} onClick={() => void complete()}>
            {submitLabel ?? (payFor === 'FULL' ? 'Complete sale' : `Complete · ${dollars(tendered)} now, ${dollars(Math.max(0, total - tendered))} due`)}
          </Button>
          <Button className="touch" disabled={busy} onClick={onClose}>
            Back
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The exchange (Phase 12, D-62): the API quotes the old tickets against the
 * cart, the cashier decides what happens to the difference (collect it or
 * have a manager waive it; refund it or record it as forfeited), and the
 * difference due is tendered like a sale. The event's exchange policy (D-66)
 * adds its fee to the difference and suggests forfeiting a downgrade; a
 * manager can waive the fee.
 */
function ExchangePanel({ cart, exchange, onClose, onDone }: { cart: CartView; exchange: ExchangeContext; onClose: () => void; onDone: (result: ExchangeResult) => void }) {
  const context = useWindowContext();
  const askPin = useManagerPin();
  const ticketIds = exchange.tickets.map((t) => t.id);
  const [waiveFee, setWaiveFee] = useState(false);
  const quote = useQuery<ExchangeQuote, ApiError>({ queryKey: ['exchange-quote', exchange.order_id, ticketIds.join(','), cart.totals.customer_total_cents, cart.totals.tickets, waiveFee], queryFn: async () => (await endpoints.exchangeQuote(exchange.order_id, ticketIds, waiveFee)).quote, retry: false });
  const [difference, setDifference] = useState<ExchangeDifference | null>(null);
  const [options, setOptions] = useState({ print: Boolean(context.data?.session?.ticket_printer_id), email: false, reason: '' });
  const [tendering, setTendering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const q = quote.data;
  const diff = q?.difference_cents ?? 0;
  const policy: ExchangeDifference = difference ?? (diff > 0 ? 'PAY' : diff < 0 ? (q?.downgrade ?? 'REFUND') : 'PAY');

  const submit = async (tenders: Tender[]) => {
    setBusy(true);
    setError(null);
    const attempt = (pin?: string) => endpoints.exchange({ order_id: exchange.order_id, ticket_ids: ticketIds, difference: policy, tenders, reason: options.reason || null, print: options.print, email: options.email, waive_fee: waiveFee, ...(pin ? { manager_pin: pin } : {}) });
    try {
      let result;
      try {
        result = await attempt();
      } catch (cause) {
        if ((cause as ApiError).code !== 'MANAGER_APPROVAL_REQUIRED') throw cause;
        const pin = await askPin(`Exchange ${exchange.tickets.length} ticket(s) on ${exchange.order_number}${policy === 'WAIVE' ? ` and waive ${dollars(diff)}` : ''}${waiveFee && q ? ` and waive the ${dollars(q.policy_fee_cents)} fee` : ''}`);
        if (!pin) throw new Error('Not approved');
        result = await attempt(pin);
      }
      onDone(result.exchange);
    } catch (cause) {
      setError((cause as Error).message);
      setBusy(false);
      setTendering(false);
    }
  };

  if (tendering && q) {
    return <TenderPanel cart={cart} amount={diff} title={`Difference due on ${exchange.order_number}`} submitLabel="Complete exchange" showDelivery={false} onClose={() => setTendering(false)} onSubmit={(tenders) => submit(tenders)} />;
  }
  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-shell-900/60 p-4" role="dialog" aria-modal="true" aria-labelledby="exchange-title">
      <div className="max-h-full w-full max-w-lg overflow-y-auto rounded-xl bg-surface p-5 text-sm">
        <div className="flex items-baseline justify-between">
          <h2 id="exchange-title" className="text-lg font-semibold">
            Exchange · {exchange.order_number}
          </h2>
          {q ? <span className={`tabular text-2xl font-semibold ${diff > 0 ? 'text-ink-900' : diff < 0 ? 'text-ok' : ''}`}>{diff === 0 ? 'Even' : diff > 0 ? `${dollars(diff)} due` : `${dollars(-diff)} back`}</span> : null}
        </div>
        {quote.error ? <div className="mt-3"><ErrorNote>{quote.error.message}</ErrorNote></div> : null}
        {error ? <div className="mt-3"><ErrorNote>{error}</ErrorNote></div> : null}
        {!q && !quote.error ? <p className="mt-3 text-ink-400">Quoting…</p> : null}
        {q ? (
          <>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-line p-3">
                <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Out</p>
                <ul className="mt-1 space-y-1">
                  {q.out.map((t) => (
                    <li key={t.ticket_id} className="flex justify-between gap-2">
                      <span>
                        {t.seat_label}
                        <span className="block text-xs text-ink-600">{t.ticket_type ?? ''}{t.performance_starts_at ? ` · ${when(t.performance_starts_at)}` : ''}</span>
                      </span>
                      <span className="tabular">{dollars(t.value_cents)}</span>
                    </li>
                  ))}
                </ul>
                <p className="tabular mt-2 border-t border-line pt-1 text-right font-semibold">{dollars(q.original_value_cents)}</p>
              </div>
              <div className="rounded-lg border border-line p-3">
                <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">In</p>
                <ul className="mt-1 space-y-1">
                  {q.in.map((t) => (
                    <li key={t.unit_id} className="flex justify-between gap-2">
                      <span>
                        {t.seat_label}
                        <span className="block text-xs text-ink-600">{t.ticket_type ?? ''} · {when(t.performance_starts_at)}</span>
                      </span>
                      <span className="tabular">{dollars(t.value_cents)}</span>
                    </li>
                  ))}
                </ul>
                <p className="tabular mt-2 border-t border-line pt-1 text-right font-semibold">{dollars(q.new_value_cents)}</p>
              </div>
            </div>
            {q.policy_fee_cents > 0 ? (
              <label className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-line p-3">
                <span>
                  Exchange fee <span className="tabular">{dollars(q.policy_fee_cents)}</span>
                  {waiveFee ? <span className="block text-xs text-ink-600">Waived (needs a manager)</span> : <span className="block text-xs text-ink-600">Included in the difference</span>}
                </span>
                <span className="flex items-center gap-2">
                  <input type="checkbox" checked={waiveFee} onChange={(e) => setWaiveFee(e.target.checked)} /> Waive
                </span>
              </label>
            ) : null}
            {diff !== 0 ? (
              <fieldset className="mt-3 space-y-1 rounded-lg border border-line p-3">
                <legend className="px-1 text-xs font-semibold tracking-wide text-ink-400 uppercase">The difference</legend>
                {diff > 0 ? (
                  <>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="difference" checked={policy === 'PAY'} onChange={() => setDifference('PAY')} /> Collect {dollars(diff)} now
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="difference" checked={policy === 'WAIVE'} onChange={() => setDifference('WAIVE')} /> Waive {dollars(diff)} (needs a manager)
                    </label>
                  </>
                ) : (
                  <>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="difference" checked={policy === 'REFUND'} onChange={() => setDifference('REFUND')} /> Refund {dollars(-diff)} to the original tender{context.data?.shift ? '' : ' (cash needs an open drawer)'}
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="difference" checked={policy === 'FORFEIT'} onChange={() => setDifference('FORFEIT')} /> Customer forfeits {dollars(-diff)}
                    </label>
                  </>
                )}
              </fieldset>
            ) : null}
            <div className="mt-3 space-y-1 border-t border-line pt-3">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={options.print} disabled={!context.data?.session?.ticket_printer_id} onChange={(e) => setOptions({ ...options, print: e.target.checked })} /> Print the new tickets now
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={options.email} onChange={(e) => setOptions({ ...options, email: e.target.checked })} /> Email the new tickets to the customer
              </label>
              <input value={options.reason} onChange={(e) => setOptions({ ...options, reason: e.target.value })} placeholder="Reason (optional)" className="touch w-full rounded-lg border border-line px-3" aria-label="Reason" />
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="primary" className="touch flex-1 justify-center text-base" disabled={busy} onClick={() => (policy === 'PAY' && diff > 0 ? setTendering(true) : void submit([]))}>
                {policy === 'PAY' && diff > 0 ? `Tender ${dollars(diff)}` : 'Complete exchange'}
              </Button>
              <Button className="touch" disabled={busy} onClick={onClose}>
                Back
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function ExchangeDone({ result, onNew }: { result: ExchangeResult; onNew: () => void }) {
  return (
    <div className="mx-auto max-w-md rounded-xl border border-line bg-surface p-6 text-center">
      <p className="text-5xl" aria-hidden="true">
        🔁
      </p>
      <h1 className="mt-2 text-xl font-semibold">Exchanged · {result.order_number}</h1>
      <p className="tabular mt-1 text-lg">
        {dollars(result.original_value_cents)} → {dollars(result.new_value_cents)}
      </p>
      {result.amount_due_cents > 0 ? <p className="tabular text-sm text-ink-600">Collected {dollars(result.amount_due_cents)}</p> : null}
      {result.change_cents > 0 ? <p className="tabular mt-3 rounded-lg bg-warn-soft py-2 text-2xl font-semibold text-warn">Change due {dollars(result.change_cents)}</p> : null}
      {result.amount_refunded_cents > 0 ? <p className="tabular mt-3 rounded-lg bg-ok-soft py-2 text-xl font-semibold text-ok">Refund {dollars(result.amount_refunded_cents)}{result.refund ? ` · ${result.refund.refund_number}` : ''}{result.refund && !result.refund.stripe.ok ? ` (card refund failed: ${result.refund.stripe.message ?? 'see alerts'})` : ''}</p> : null}
      {result.adjustment_cents !== 0 ? <p className="text-sm text-ink-600">{result.adjustment_cents < 0 ? `Waived ${dollars(-result.adjustment_cents)}` : `Forfeited ${dollars(result.adjustment_cents)}`}</p> : null}
      <p className="mt-3 text-sm text-ink-600">
        {result.new_ticket_ids.length} new ticket{result.new_ticket_ids.length === 1 ? '' : 's'}
        {result.print_job_ids.length > 0 ? ` · ${result.print_job_ids.length} printing` : ''}
      </p>
      <div className="mt-5 flex justify-center gap-2">
        <Button variant="primary" className="touch text-base" onClick={onNew}>
          Done
        </Button>
        <Button className="touch" onClick={() => void endpoints.printOrder(result.order_id)}>
          Print the order
        </Button>
      </div>
    </div>
  );
}

function SaleDone({ result, onNew }: { result: SaleResult; onNew: () => void }) {
  return (
    <div className="mx-auto max-w-md rounded-xl border border-line bg-surface p-6 text-center">
      <p className="text-5xl" aria-hidden="true">
        ✅
      </p>
      <h1 className="mt-2 text-xl font-semibold">Sold · {result.order_number}</h1>
      <p className="tabular mt-1 text-3xl font-semibold">{dollars(result.total_cents)}</p>
      {result.change_cents > 0 ? <p className="tabular mt-3 rounded-lg bg-warn-soft py-2 text-2xl font-semibold text-warn">Change due {dollars(result.change_cents)}</p> : null}
      <p className="mt-3 text-sm text-ink-600">
        {result.ticket_ids.length} ticket{result.ticket_ids.length === 1 ? '' : 's'}
        {result.print_job_ids.length > 0 ? ` · ${result.print_job_ids.length} printing` : ''}
        {result.emailed ? ' · emailed' : ''}
        {result.receipt_job_id ? ' · receipt printing' : ''}
      </p>
      <div className="mt-5 flex justify-center gap-2">
        <Button variant="primary" className="touch text-base" onClick={onNew}>
          Next sale
        </Button>
        <Button className="touch" onClick={() => void endpoints.printOrder(result.order_id)}>
          Print again
        </Button>
      </div>
    </div>
  );
}

export type { WindowPerformance };
